/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { FC, useMemo, useCallback, useState, useEffect, useRef } from "react";
import usePortal from "react-useportal";
import { ModuleRegistry, provideGlobalGridOptions } from "ag-grid-community";
import { AllEnterpriseModule, IntegratedChartsModule } from "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import { AgChartsEnterpriseModule } from "ag-charts-enterprise";

// antd:
import { Form, Select, Divider, Space, Input, Button, message } from "antd";
import type { InputRef } from "antd";

import { PlusOutlined } from "@ant-design/icons";

// eslint-disable-next-line node/no-missing-import
import type * as CSS from "csstype";

// My Components:
import AppIcons from "../react/AppIcons.tsx";
import SpanE from "../react/SpanE.tsx";
import DivE from "../react/DivE.tsx";
import ModalWindow from "../react/ModalWindow.tsx";
// Redux-hooks:
import { useAppSelector, useAppDispatch } from "../../libs/redux-hooks.ts"; // useAppDispatch
// Reducers:
import { getAppState, changeAppState, getTheme, getLng, getDict, getUser, getSafePtr, getCompact, getDesktopType } from "../../libs/reducer.ts";
// My functions
import fnc from "../../libs/fnc.ts";
// Types:
import type {TuniversalElementProps} from "../../types/Iuigen.d.ts";
import type {Tpermissions} from "../../types/easyTypes.ts";

interface Option {
  value:string;
  label:string;
  disabled?:boolean;
  options?:Option[]
}

interface ModalGridRow {
  colName:string;
  title:string;
  selected:boolean;
  disabled?:boolean;
}

type RequiredMark = boolean | "optional" | "customize";

type Tselect = {
  allowClear:boolean,
  size:"large" | "middle" | "small",
  mode?:"multiple" | "tags",
  uservalue?:boolean,
  autoClearSearchValue?:boolean,
  autoFocus?:boolean,
  variant:"outlined" | "borderless" | "filled",
  filterOption?:boolean,
  maxCount?:number,
  maxTagCount?:number | "responsive",
  placement:"bottomLeft" | "bottomRight" | "topLeft" | "topRight",
  showSearch?:boolean, // by default - single: false, multiple: true
  status?:"error" | "warning",
  virtual?:boolean,
  listItemHeight?:number,
  listHeight?:number,
  required?:RequiredMark,
  rules?:Array<{[key:string]:any}>,
  selectOnModalButton?:boolean,
  modalWidth?:number,
  modalHeight?:number,
  // пропс определяет должен ли dropdown совпадать по ширине с самим select
  popupMatchSelectWidth?:boolean | number,
  modalFullscreen?:boolean
};

const { rz } = window;

ModuleRegistry.registerModules([
  AllEnterpriseModule,
  IntegratedChartsModule.with(AgChartsEnterpriseModule)
]);
provideGlobalGridOptions({ theme: "legacy"});

const RZselect:FC<TuniversalElementProps> = ({attributes, children}) => {
  RZselect.displayName = "Select";
  const dispatch = useAppDispatch();
  const user = useAppSelector(getUser);
  const dct = useAppSelector(getDict);
  const lng = useAppSelector(getLng);
  const theme = useAppSelector(getTheme);
  const compact = useAppSelector(getCompact);
  const isMobile = useAppSelector(getDesktopType);
  const safe = useAppSelector(getSafePtr);
  const rlng = (lng === "en" ? "ru" : "en");

  const {rootAttributes, key, fullPath, type, content, hasitems} = attributes; // onClick, onChange, onRightClick, onHover, onLoad
  const appState = useAppSelector(getAppState(rootAttributes.key)), elState = appState[fullPath]; // value, visible, disabled, loading, userStyle, userClass
  const {icon, iconClass, iconColor} = elState.attributes;
  const rootNamespace = useMemo(() => (window[rootAttributes?.key || rz.strMakeID(24)]), [rootAttributes?.key]);
  const isParentForm = useMemo(() => {
    const parentPath = rz.strParentFolderName(fullPath || ""), parentState = appState[parentPath];
    return Boolean(parentState.name === "form");
  }, [appState, fullPath]);

  const permissions = useMemo<Tpermissions>(() => (fnc.checkPermissions(user, attributes)), [user, attributes]);

  const selectRef = useRef<InputRef>(null);
  const modalGridRef = useRef<AgGridReact<any>>(null);
  const [newItem, setNewItem] = useState("");
  const [newItems, setNewItems] = useState<Array<string>>([]);

  const [showModal, setShowModal] = useState<boolean>(false);
  const [modalSearch, setModalSearch] = useState<string>("");

  const clsu = useMemo<Array<string>>(() => {
    const userClss = rz.objClone(elState?.userClass || []);
    // if ((elState?.disabled || permissions.disabled === true) && !~userClss.indexOf("uk-disabled")) { userClss.push("uk-disabled") }
    return userClss;
  }, [elState?.userClass]);

  const styl = useMemo<CSS.Properties>(() => {
    const est = {...elState.userStyle};
    if ((elState?.visible === false || permissions.visible === false) && est.display !== "none") { est.display = "none" }
    else if ((elState?.visible === true && permissions.visible !== false) && est.display === "none") { est.display = undefined }
    return est;
  }, [elState?.userStyle, elState?.visible, permissions]);

  const rico = useMemo(() => {
    let ico:React.ReactElement | undefined;
    if (icon) { ico = (<AppIcons key={key + "-icon"} icopath={icon} attrs={{className: iconClass || "rz-margin-right-5", color: iconColor}} />) }
    return ico;
  }, [key, icon, iconClass, iconColor]);

  const prefix = useMemo(() => {
    if (elState.attributes.prefixIcon) {
      return (<AppIcons icopath={elState.attributes.prefixIcon} attrs={{className: "rz-margin-right-5"}}/>)
    }
    if (elState.attributes.prefixText) {
      return <span className="rz-margin-right-5">{elState.attributes.prefixText}</span>;
    }
    return null;
  }, [elState.attributes.prefixIcon, elState.attributes.prefixText])

  const contentWithIcon = useCallback(({itemValue, itemIcon, itemIconClass, itemIconColor, itemTooltip}:{itemValue:string, itemIcon?:string, itemIconClass?:string, itemIconColor?:string, itemTooltip?:string}) => {
    let ico:React.ReactElement | undefined;
    if (itemIcon) { ico = (<AppIcons icopath={itemIcon} attrs={{className: itemIconClass || "rz-margin-right-5", color: itemIconColor}} />) }
    return (
      <>
        {ico}
        <SpanE key={(itemValue || "").replace((/\s+/igm), "") + "-span"} uk-tooltip={itemTooltip} >
          {itemValue}
        </SpanE>
      </>
    );
  }, []);

  const selectType = useMemo<Tselect>(() => {
    const typ:Tselect = {
      allowClear: true, autoFocus: false, variant: "outlined", placement: "bottomLeft", size: "middle", filterOption: true, uservalue: false, virtual: false,
      modalFullscreen: isMobile
    };
    (type || "").replace((/(\s+|_|\|)/igm), " ").split(" ").forEach((tt) => {
      if (~["large", "middle", "small"].indexOf(tt)) { typ.size = tt }
      else if (tt === "disallowClear") { typ.allowClear = false }
      else if (tt === "autoFocus") { typ.autoFocus = true }
      else if (~["noborders", "borderless"].indexOf(tt)) { typ.variant = "borderless" }
      else if (~["filled"].indexOf(tt)) { typ.variant = "filled" }
      else if (tt === "filterOption") { typ.filterOption = true }
      else if (tt === "showSearch") { typ.showSearch = true }
      else if (tt === "required") {
        typ.required = true;
        typ.rules = [{required: true, message: (attributes["requiredText_" + lng] || attributes.requiredText || attributes["requiredText_" + rlng] || "") || "Required field!"}];
      }
      else if (~["uservalue", "userValue"].indexOf(tt)) { typ.uservalue = true }
      else if (~["tags", "multiple", "single"].indexOf(tt)) { typ.mode = (tt === "single" ? undefined : tt) }
      else if (~["bottomLeft", "bottomRight", "topLeft", "topRight"].indexOf(tt)) { typ.placement = tt }
      else if (tt.startsWith("maxTagCount-")) { const mt = tt.split("-")[1]; typ.maxTagCount = (mt === "responsive" ? mt : Number(mt)) }
      else if (tt.startsWith("maxCount-")) { const mt = tt.split("-")[1]; typ.maxCount = Number(mt) }
      else if (~["selectOnModal", "selectOnModalButton"].indexOf(tt)) { typ.selectOnModalButton = true }
      else if (~["modalFullscreen", "modalfullscreen"].indexOf(tt)) { typ.modalFullscreen = true }
    });

    if (attributes?.size && ~["large", "middle", "small"].indexOf(attributes.size)) { typ.size = attributes.size }
    if (attributes?.multiple === "true") { typ.mode = "multiple" }
    if (attributes?.allowClear === "true") { typ.allowClear = true } else if (attributes?.allowClear === "false") { typ.allowClear = false }
    if (attributes?.virtual === "true") { typ.virtual = true } else if (attributes?.virtual === "false") { typ.virtual = false }
    if (attributes?.showSearch === "true") { typ.showSearch = true } else if (attributes?.showSearch === "false") { typ.showSearch = false }
    if (attributes?.variant) { typ.variant = attributes.variant }
    if (attributes?.filterOption === "true") { typ.filterOption = true } else if (attributes?.filterOption === "false") { typ.filterOption = false }
    if (attributes?.userValue === "true") { typ.uservalue = true } else if (attributes?.userValue === "false") { typ.uservalue = false }
    if (attributes?.placement && ~["bottomLeft", "bottomRight", "topLeft", "topRight"].indexOf(attributes.placement)) { typ.placement = attributes.placement }
    if (attributes?.listHeight) { typ.listHeight = Number(attributes.listHeight) }
    if (attributes?.listItemHeight) { typ.listItemHeight = Number(attributes.listItemHeight) }
    if (attributes?.maxTagCount) { typ.maxTagCount = (attributes.maxTagCount === "responsive" ? attributes.maxTagCount : Number(attributes.maxTagCount)) }
    if (attributes?.maxCount) { typ.maxCount = Number(attributes.maxCount) }
    if (attributes?.required) {
      if (attributes.required.startsWith("{") && attributes.required.endsWith("}")) {
        const jsnb = (rz.strReplaceAll(attributes.required, "'", "\"")).trim(); let jsn; try { jsn = JSON.parse(jsnb) } catch (err) { console.log(err) }
        if (jsn) {
          const rules = jsn[rz.objKeys(jsn)[0]];
          if (rules && rz.isArray(rules)) {
            rules.forEach(((rr) => {
              const rt = attributes["requiredtext_" + lng] || attributes["requiredText_" + lng];
              if (rt && !rr.message) { rr.message = rt }
            }));
            typ.required = true; typ.rules = rules;
          }
        }
      }
      else {
        typ.required = (attributes.required === "true");
        if (typ.required) typ.rules = [{required: true, message: (attributes["requiredText_" + lng] || attributes.requiredText || attributes["requiredText_" + rlng] || "") || "Required field!"}];
      }
    }
    if (attributes?.selectOnModalButton) { typ.selectOnModalButton = (attributes.selectOnModalButton === "true") }
    else if (attributes?.selectOnModal) { typ.selectOnModalButton = (attributes.selectOnModal === "true") }
    if (attributes?.modalFullscreen) { typ.modalFullscreen = (attributes.modalFullscreen === "true") }
    if (attributes?.modalWidth) { typ.modalWidth = Number(attributes.modalWidth) }
    if (attributes?.modalHeight) { typ.modalHeight = Number(attributes.modalHeight) }
    if (attributes?.popupMatchSelectWidth) {
      if (rz.arrContains(["true", "false"], attributes.popupMatchSelectWidth)) { typ.popupMatchSelectWidth = (attributes.popupMatchSelectWidth === "true") }
      else if (rz.isNumeric(attributes.popupMatchSelectWidth)) { typ.popupMatchSelectWidth = Number(attributes.popupMatchSelectWidth) }
    }

    return typ;
  }, [isMobile, type, attributes, lng, rlng]);

  const subStyle = useMemo(() => {
    const smp = {padding: 0, marginLeft: 0, marginRight: 0, height: 30, iconWidth: 20, fontSize: "1.4em"};
    if (compact) { smp.marginLeft = 0; smp.marginRight = 0 }
    switch (selectType.size) {
      case "small": smp.height = (compact ? 20 : 22); smp.iconWidth = 14; break;
      case "large": smp.height = (compact ? 34 : 39); smp.iconWidth = 16; break;
      default: smp.height = (compact ? 28 : 30); smp.iconWidth = 14;
    }
    return smp;
  }, [compact, selectType.size]);

  // eslint-disable-next-line react-hooks/exhaustive-deps, max-len
  const title = useMemo<string | undefined>(() => (elState.attributes["title_" + lng] || elState.attributes.title || elState.attributes["title_" + rlng] || attributes["title_" + lng] || attributes.title || attributes["title_" + rlng]), [elState.attributes, attributes, lng]);
  // eslint-disable-next-line max-len, react-hooks/exhaustive-deps
  const title2 = useMemo<string | undefined>(() => (elState?.attributes["title2_" + lng] || elState?.attributes.title2 || elState?.attributes["title2_" + rlng] || attributes["title2_" + lng] || attributes.title2 || attributes["title2_" + rlng]), [elState?.attributes, attributes, lng]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const tooltip = useMemo<string | undefined>(() => (elState.attributes["tooltip_" + lng] || elState.attributes.tooltip || elState.attributes["tooltip_" + rlng]), [elState.attributes, lng]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const placeholder = useMemo<string | undefined>(() => (
    elState?.attributes["placeholder_" + lng] || elState?.attributes.placeholder || elState?.attributes["placeholder_" + rlng]
    || attributes["placeholder_" + lng] || attributes.placeholder || attributes["placeholder_" + rlng]
  ), [elState?.attributes, attributes, lng, rlng]);

  const items = useMemo<Array<Option>>(() => {
    let itms:Array<Option> = [];
    const sitms = elState?.items;
    if (sitms) {
      if (rz.isArray(sitms)) {
        itms = sitms.map((aa, a) => {
          let chld1; const perm1 = fnc.checkPermissions(user, {...aa?.attributes, rootAttributes: {key: rootAttributes.key}});
          if (aa?.children && aa.children.length) {
            chld1 = aa.children.map((bb, b) => {
              const perm2 = fnc.checkPermissions(user, {...bb?.attributes, rootAttributes: {key: rootAttributes.key}});
              return {
                value: (rz.isString(bb) ? bb : bb.value || bb.key || (key + "-" + b)),
                label: contentWithIcon({
                  itemValue: (rz.isString(bb) ? bb : (bb["title_" + lng] || bb.title || bb["title_" + rlng] || bb["label_" + lng] || bb.label || bb["label_" + rlng] || bb.value || "")),
                  itemIcon: bb?.icon, itemIconClass: bb?.iconClass, itemIconColor: bb?.iconColor, itemTooltip: bb?.toltip
                }),
                disabled: (perm2.disabled === true)
              };
            });
            return {
              value: (rz.isString(aa) ? aa : aa.value || aa.key || (key + "-" + a)),
              label: contentWithIcon({
                itemValue: (rz.isString(aa) ? aa : (aa["title_" + lng] || aa.title || aa["title_" + rlng] || aa["label_" + lng] || aa.label || aa["label_" + rlng] || aa.value || "")),
                itemIcon: aa?.icon, itemIconClass: aa?.iconClass, itemIconColor: aa?.iconColor, itemTooltip: aa?.toltip
              }),
              disabled: (perm1.disabled === true),
              options: chld1
            };
          }
          return {
            value: (rz.isString(aa) ? aa : (aa.value || aa.key || (key + "-" + a))),
            label: contentWithIcon({
              itemValue: (rz.isString(aa) ? aa : (aa["title_" + lng] || aa.title || aa["title_" + rlng] || aa["label_" + lng] || aa.label || aa["label_" + rlng] || aa.value || "")),
              itemIcon: aa?.icon, itemIconClass: aa?.iconClass, itemIconColor: aa?.iconColor, itemTooltip: aa?.toltip
            }),
            disabled: (perm1.disabled === true)
          };
        });
      }
      else if (rz.isString(sitms)) { itms = (sitms.split("|")).map((value) => ({value, label: value})) }
    }
    else if (hasitems && content && rz.isArray(content)) {
      itms = content.map((aa, a) => {
        let chld1; const perm1 = fnc.checkPermissions(user, {...aa?.attributes, rootAttributes: {key: rootAttributes.key}}), state1 = appState[aa?.fullPath];
        if (aa?.content && aa.content.length) {
          chld1 = aa.content.map((bb, b) => {
            const perm2 = fnc.checkPermissions(user, {...bb?.attributes, rootAttributes: {key: rootAttributes.key}}), state2 = appState[bb.fullPath];
            return {
              value: bb.value || bb.key || (key + "-" + b),
              label: contentWithIcon({
                itemValue: bb.attributes["title_" + lng] || bb.attributes.title || bb.attributes["title_" + rlng] || bb.attributes["label_" + lng] || bb.attributes.label || bb.attributes["label_" + rlng] || "",
                itemIcon: bb.attributes?.icon, itemIconClass: bb.attributes?.iconClass, itemIconColor: bb.attributes?.iconColor, itemTooltip: bb.attributes?.toltip
              }),
              disabled: (state2?.disabled || perm2.disabled === true)
            };
          });
          return {
            value: aa.value || aa.key || (key + "-" + a),
            label: contentWithIcon({
              itemValue: aa.attributes["title_" + lng] || aa.attributes.title || aa.attributes["title_" + rlng] || aa.attributes["label_" + lng] || aa.attributes.label || aa.attributes["label_" + rlng] || "",
              itemIcon: aa.attributes?.icon, itemIconClass: aa.attributes?.iconClass, itemIconColor: aa.attributes?.iconColor, itemTooltip: aa.attributes?.toltip
            }),
            disabled: (state1?.disabled || perm1.disabled === true),
            options: chld1
          };
        }
        return {
          value: (rz.isString(aa) ? aa : (aa.value || aa.key || (key + "-" + a))),
          label: contentWithIcon({
            itemValue: (rz.isString(aa) ? aa : (aa.attributes["title_" + lng] || aa.attributes.title || aa.attributes["title_" + rlng] || aa.attributes["label_" + lng] || aa.attributes.label || aa.attributes["label_" + rlng] || "")),
            itemIcon: aa.attributes?.icon, itemIconClass: aa.attributes?.iconClass, itemIconColor: aa.attributes?.iconColor, itemTooltip: aa.attributes?.toltip
          }),
          disabled: (state1?.disabled || perm1.disabled === true)
        };
      });
    }
    // User-values:
    if (newItems && newItems.length) {
      newItems.forEach((ni:string) => {
        if (!~rz.arrFindObj(itms, {value: ni})) { itms.push({label: ni, value: ni}) }
      });
    }
    return itms;
  }, [elState?.items, hasitems, content, newItems, user, rootAttributes.key, key, contentWithIcon, lng, rlng, appState]);

  const optionLabelText = useCallback((opt:Option) => {
    if (!opt) return "";
    if (rz.isString(opt.label)) return opt.label;
    const lbl:any = opt.label;
    if (lbl?.props?.children) {
      const ch = Array.isArray(lbl.props.children) ? lbl.props.children : [lbl.props.children];
      const txt = ch.map((cc:any) => {
        if (rz.isString(cc)) return cc;
        if (cc?.props?.children && rz.isString(cc.props.children)) return cc.props.children;
        return "";
      }).filter(Boolean).join(" ").trim();
      if (txt) return txt;
    }
    return String(opt.value || "");
  }, []);

  const selectedValuesForModal = useMemo<Array<string>>(() => {
    const vlu = elState?.value;
    if (rz.isArray(vlu)) {
      // @ts-ignore
      return ((~["multiple", "tags"].indexOf(selectType.mode) ? vlu : [vlu[0]])).map((vv) => String(vv));
    }
    if (rz.isString(vlu)) {
      // @ts-ignore
      return (~["multiple", "tags"].indexOf(selectType.mode) ? [vlu] : [vlu]);
    }
    return [];
  }, [elState?.value, selectType.mode]);

  const modalGridRows = useMemo<Array<ModalGridRow>>(() => {
    const selectedSet = new Set(selectedValuesForModal);
    const rows:Array<ModalGridRow> = [];
    const walk = (arr?:Array<Option>) => {
      if (!arr || !arr.length) return;
      arr.forEach((it) => {
        const labelText = optionLabelText(it);
        if (it?.options && it.options.length) {
          walk(it.options);
        }
        else if (rz.isString(it?.value)) {
          const colName = String(it.value);
          rows.push({
            colName,
            title: labelText,
            selected: selectedSet.has(colName),
            disabled: Boolean(it.disabled)
          });
        }
      });
    };
    walk(items);
    return rows;
  }, [items, optionLabelText, selectedValuesForModal]);

  const isMultiMode = useMemo(() => (~["multiple", "tags"].indexOf(selectType.mode as string)), [selectType.mode]);

  const rowSelection = useMemo(() => {
    if (isMultiMode) {
      return {
        mode: "multiRow" as const, checkboxes: true, headerCheckbox: true,
        enableMultiSelectWithClick: true, enableClickSelection: true, enableSelectionWithoutKeys: true,
        groupSelects: "filteredDescendants" as const
      };
    }
    return {
      mode: "singleRow" as const, checkboxes: true, headerCheckbox: false,
      enableMultiSelectWithClick: false, enableClickSelection: true,
      groupSelects: "filteredDescendants" as const
    };
  }, [isMultiMode]);

  const modalGridColumns = useMemo(() => ([
    {
      field: "colName",
      headerName: ({ru: "Элемент", en: "Item", tr: "Öğe"}[String(lng || "en")] || "Item"),
      flex: 1,
      sortable: true,
      valueGetter: (params) => (params?.data?.title || params?.data?.colName || "")
    }
  ]), [lng]);

  const filterOption = useCallback((inputValue:string, option:any) => {
    if (selectType.filterOption === false) { return false }
    let item = (rz.isObject(option) ? (option.title || option.label || option.value || option.key) : (option || ""));
    if (rz.isObject(item) && item.props && item.props.children && item.props.children.length) { // If we have a ReactElement inside 'option'
      const chlds = item.props.children.filter((ch) => (ch && ch.props && ch.props.children && rz.isString(ch.props.children))).map((ch) => (ch.props.children));
      if (chlds && chlds.length) { item = chlds[0] }
    }
    return rz.strCompare(item, inputValue, ".caseNoSensitive.toLowerCase.trim.substring");
  }, [selectType.filterOption]);

  const selValue = useMemo<Option | Array<Option> | undefined>(() => {
    let defval:Option | Array<Option>;
    const vlu = elState?.value;
    if (vlu) {
      if (rz.isArray(vlu)) { defval = [...vlu] } // vlu.filter((value) => (~rz.arrFindObj(items, {value})))
      else { defval = vlu }
      // else if (rz.isString(vlu)) { defval = (vlu.split("|")).map((value) => (value)) } // НЕЛЬЗЯ ТАК
    }
    // @ts-ignore
    if ((!~["multiple", "tags"].indexOf(selectType.mode)) && rz.isArray(defval) && defval.length) { return defval[0] }
    // @ts-ignore
    if (~["multiple", "tags"].indexOf(selectType.mode) && rz.isString(defval)) { return [defval] }
    // @ts-ignore
    return defval;
  }, [elState?.value, selectType.mode]);

  const onChange = useCallback((value: Option | Option[], option: Option | Option[]) => {
    if (fnc.config?.isDev) console.log({action: "<Select key='" + key + "'> -> onChange()", value, option});
    dispatch(changeAppState({appkey: rootAttributes.key, fullPath, newState: {...elState, value}}));
    if (rootNamespace && (attributes.onChange || attributes.onchange) && rootNamespace[attributes.onChange || attributes.onchange]) {
      const clientFNC = rootNamespace[attributes.onChange || attributes.onchange];
      if (rz.isPromise(clientFNC)) {
        clientFNC({appkey: rootAttributes.key, attributes, key, user, lng, dct, value, safe}).then((ret) => {
          if (rz.isUndefinedOrNull(ret)) { return }
          if (ret?.err) { console.log(ret.err) }
          if (ret && ret?.state?.fullPath && ret?.state?.newState) {
            dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
          }
        });
      }
      else if (rz.isFunction(clientFNC)) {
        const ret = clientFNC({appkey: rootAttributes.key, attributes, key, user, lng, dct, value, safe}); if (rz.isUndefinedOrNull(ret)) { return }
        if (ret?.err) { console.log(ret.err) }
        if (ret && ret?.state?.fullPath && ret?.state?.newState) {
          dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
        }
      }
    }
  }, [key, dispatch, rootAttributes.key, fullPath, elState, rootNamespace, attributes, user, lng, dct, safe]);

  useEffect(() => {
    if (!showModal) return;
    setModalSearch("");
    modalGridRef.current?.api?.setGridOption("quickFilterText", "");
  }, [showModal]);

  const applyModalSelection = useCallback(() => {
    const selectedNodes = modalGridRef.current?.api?.getSelectedNodes() || [];
    const selectedRows = selectedNodes.map((rr:any) => String(rr?.data?.colName || "")).filter(Boolean);
    const value = (isMultiMode ? selectedRows : (selectedRows[0] || undefined));
    // @ts-ignore
    onChange(value, []);
    setShowModal(false);
  }, [isMultiMode, onChange]);

  const onNameChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    if (fnc.config?.isDev) console.log({action: "<Select key='" + key + "'> -> <Input key='customItem'> -> onChange()", value: event.target.value});
    setNewItem(event.target.value);
  }, [key]);

  const addItem = useCallback((event) => {
    if (event.preventDefault) event.preventDefault();
    if (fnc.config?.isDev) console.log({action: "<Select key='" + key + "'> -> <Button key='addCustomItem'> -> onClick()", value: newItem});
    if (newItem && !~newItems.indexOf(newItem) && !~rz.arrFindObj(items, {value: newItem})) {
      setNewItems([...newItems, newItem]);
      setNewItem("");
      if (rootNamespace && (attributes.additem || attributes.addItem) && rootNamespace[attributes.additem || attributes.addItem]) {
        const clientFNC = rootNamespace[attributes.additem || attributes.addItem];
        if (rz.isPromise(clientFNC)) {
          clientFNC({appkey: rootAttributes.key, attributes, key, user, lng, dct, value: newItem, safe}).then((ret) => {
            if (rz.isUndefinedOrNull(ret)) { return }
            if (ret?.err) { console.log(ret.err) }
            if (ret && ret?.state?.fullPath && ret?.state?.newState) {
              dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
            }
          });
        }
        else if (rz.isFunction(clientFNC)) {
          const ret = clientFNC({appkey: rootAttributes.key, attributes, key, user, lng, dct, value: newItem, safe}); if (rz.isUndefinedOrNull(ret)) { return }
          if (ret?.err) { console.log(ret.err) }
          if (ret && ret?.state?.fullPath && ret?.state?.newState) {
            dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
          }
        }
      }
    }
    else {
      message.warning(React.createElement(SpanE, {className: "txt-black"}, [(lng === "en" ? "Cant add this Item (empty or already exist)" : "Не могу добавить этот элемент (пустой или уже существует)")]), 3);
    }
    setTimeout(() => { selectRef.current?.focus() }, 50);
  }, [attributes, dct, dispatch, items, key, lng, newItem, newItems, rootAttributes.key, rootNamespace, user, safe]);

  const dropdownRender = useCallback((menu:React.ReactNode) => {
    let newItemContent;
    if (selectType.uservalue && permissions.created !== false) {
      newItemContent = (
        // onMouseDown={(e) => { e.preventDefault(); e.stopPropagation() }}
        <div key={key + "-dropdown-render-div"}>
          <Divider style={{ margin: "8px 0" }} />
          <Space style={{ padding: "0 8px 4px" }}>
            <Input key={key + "-input-new-item"}
              placeholder="Please enter item"
              ref={selectRef}
              value={newItem}
              onChange={onNameChange}
              onKeyDown={(e) => e.stopPropagation()}
              onPressEnter={addItem}
              onClick={(e) => { e.stopPropagation(); e.nativeEvent.stopImmediatePropagation(); }}
            />
            <Button key={key + "-button-new-item"} type="text" icon={<PlusOutlined />} onClick={addItem}>
              {lng === "ru" ? "Добавить элемент" : "Add item"}
            </Button>
          </Space>
        </div>
      );
    }

    return (
      <>
        {menu}
        {newItemContent}
      </>
    );
  }, [selectType.uservalue, permissions.created, key, newItem, onNameChange, addItem, lng]);

  const wrapperForm = useCallback((mainComponent:React.ReactNode) => {
    if (isParentForm) {
      const frmItemProps:{[key:string]:any} = {
        key: key + "-formitem", label: (<span className="rz-txt-std">{title2}</span>), className: "rz-width-expand",
        style: ((elState?.visible === false || permissions.visible === false) ? {display: "none"} : undefined)
      };
      if (selectType.rules) { frmItemProps.name = String(key); frmItemProps.rules = selectType.rules; frmItemProps.getValueProps = () => (elState.value) }
      return (
        <Form.Item {...frmItemProps}>
          {mainComponent}
        </Form.Item>
      )
    }
    return mainComponent;
  }, [isParentForm, title2, key, selectType.rules, elState?.visible, elState.value, permissions.visible]);

  const showModalButtonClick = useCallback((prms) => {
    if (fnc.config?.isDev) console.log({action: "<Select key='" + key + "'> -> showModalButtonClick()", items: prms.items, selValue: prms.selValue});

    if (rootNamespace && (attributes.onShowModalSelect || attributes.onshowmodalselect) && rootNamespace[attributes.onShowModalSelect || attributes.onshowmodalselect]) {
      const clientFNC = rootNamespace[attributes.onShowModalSelect || attributes.onshowmodalselect];
      if (rz.isPromise(clientFNC)) {
        clientFNC({appkey: rootAttributes.key, attributes, key, user, lng, dct, items: prms.items, selValue: prms.selValue, safe}).then((ret) => { if (ret?.err) { console.log(ret.err) } });
      }
      else if (rz.isFunction(clientFNC)) {
        const ret = clientFNC({appkey: rootAttributes.key, attributes, key, user, lng, dct, items: prms.items, selValue: prms.selValue, safe}); if (ret?.err) { console.log(ret.err) }
      }
    }
    else {
      // Internal modal with Tree
      setShowModal(true);
    }
  }, [attributes, dct, key, lng, rootAttributes.key, rootNamespace, safe, user]);

  // eslint-disable-next-line destructuring/no-rename
  const { ref: divRef, openPortal, closePortal, isOpen, Portal } = usePortal({closeOnOutsideClick: true, closeOnEsc: true});
  const portalRef = useRef(null);

  useEffect(() => {
    if (showModal && !isOpen) { openPortal() }
    else if (!showModal && isOpen) { closePortal() }
  }, [elState.visible, permissions.visible, closePortal, isOpen, openPortal, showModal]);

  // Debug
  // if (key === "cf-lab_techgroups") console.log({items, selValue});
  // useEffect(() => {
  //   console.log("listHeight debug:", {
  //     elStateAttributesListHeight: elState.attributes.listHeight,
  //     selectTypeListHeight: selectType.listHeight,
  //     attributes
  //   });
  // }, [elState.attributes, selectType.listHeight, attributes]);

  if (selectType.selectOnModalButton) {
    return (
      wrapperForm(
        <DivE key={key + "-parent-div"} uk-tooltip={tooltip}>
          <Space.Compact key={key + "-space-compact"} block style={{padding: subStyle.padding, marginLeft: subStyle.marginLeft, marginRight: subStyle.marginRight, height: "auto"}}>
            <Select
              key={key} style={styl} className={clsu.join(" ")}
              options={items}
              value={selValue}
              // defaultValue={selValue}
              mode={selectType.mode}
              size={selectType.size}
              prefix={prefix}
              suffixIcon={rico}
              onChange={onChange}
              filterOption={filterOption}
              placeholder={placeholder}
              placement={selectType.placement}
              allowClear={selectType.allowClear}
              showSearch={selectType.showSearch}
              autoFocus={selectType.autoFocus}
              variant={selectType.variant}
              virtual={selectType.virtual}
              maxCount={selectType.maxCount}
              maxTagCount={selectType.maxTagCount}
              popupMatchSelectWidth={selectType.popupMatchSelectWidth}
              listHeight={Number(elState.attributes.listHeight) || selectType.listHeight}
              listItemHeight={elState.attributes.listItemHeight || selectType.listItemHeight}
              disabled={elState?.disabled || permissions.disabled === true}
              popupRender={dropdownRender}
              // getPopupContainer={(trigger) => (document.getElementById("virtual-desktop"))}
            />
            <Button key={key + "-show-modal-button"}
              type={"primary"} size={selectType.size || "middle"}
              uk-tooltip={{ru: "Выбрать элементы в списке на модальном окне", en: "Select items in the list on the modal window", tr: "Modal pencerede listedeki öğeleri seçin"}[lng || "en"]}
              style={{minWidth: "2em", paddingTop: 0, paddingBottom: 0, height: "auto"}} // borderTopRightRadius: "0", borderBottomRightRadius: "0"
              disabled={elState?.disabled || permissions.disabled === true}
              onClick={() => { showModalButtonClick({items, selValue}) }}
            >
              <AppIcons key={key + "-show-modal-button-icon"} icopath={"tb/TbWindowMaximize"} style={{}} attrs={{size: "1.4em"}} />
            </Button>
          </Space.Compact>
          <div key={key + "-dummy-div"} ref={divRef} />
          {isOpen && selectType.selectOnModalButton && (
            <Portal key={key + "-portal"} ref={portalRef} style={{height: "100vh", width: "100vw"}} >
              <ModalWindow modalKey={key}
                isOpen={Boolean(showModal)} onRequestClose={() => { setShowModal(false) }}
                initWidth={selectType.modalWidth || 620} initHeight={selectType.modalHeight || 580} minWidth={400} minHeight={400}
                title={dct.p.selectItems} icon={ <AppIcons key={key + "-modal-icon"} icopath={"tb/TbWindowMaximize"} style={{}} attrs={{}} />}
                dct={dct} closeOnEsc={true} hasResizeable={!isMobile && selectType.modalFullscreen !== true} hasMoveable={!isMobile && selectType.modalFullscreen !== true}
                isMaximize={isMobile || selectType.modalFullscreen === true} hasMaximize={!isMobile && selectType.modalFullscreen !== true}
                className="uk-flex uk-flex-center uk-flex-middle rz-theme-d3" zIndex={1500}
              >
                <div className="uk-flex uk-flex-column" style={{height: "100%", width: "100%"}}>
                  <div style={{padding: 12, paddingBottom: 8}}>
                    <Input.Search
                      allowClear
                      value={modalSearch}
                      onChange={(e) => {
                        const { value } = e.target;
                        setModalSearch(value);
                        modalGridRef.current?.api?.setGridOption("quickFilterText", value);
                      }}
                      placeholder={(lng === "ru" ? "Поиск по элементам" : "Search items")}
                    />
                  </div>
                  <div style={{flex: 1, minHeight: 0, padding: "0 12px 12px 12px"}}>
                    <div
                      className={"ag-theme-" + (compact ? "balham" : "alpine") + (theme === "dark" ? "-dark" : "")}
                      style={{height: "100%", width: "100%"}}
                    >
                      <AgGridReact
                        ref={modalGridRef}
                        rowData={modalGridRows}
                        columnDefs={modalGridColumns}
                        rowSelection={rowSelection}
                        includeHiddenColumnsInQuickFilter={true}
                        getRowId={(params) => String(params.data?.colName || "")}
                        onGridReady={() => {
                          if (modalSearch) { modalGridRef.current?.api?.setGridOption("quickFilterText", modalSearch) }
                        }}
                        onFirstDataRendered={() => {
                          setTimeout(() => {
                            modalGridRef.current?.api?.forEachNode((node) => { node.setSelected(Boolean(node?.data?.selected)) });
                          }, 500);
                        }}
                      />
                    </div>
                  </div>
                  <div className="uk-flex" style={{justifyContent: "space-between", gap: 8, padding: 12, borderTop: "1px solid var(--rz-border-color, #e8e8e8)"}}>
                    <Button onClick={() => { setShowModal(false) }}>
                      {lng === "ru" ? "Отмена" : "Cancel"}
                    </Button>
                    <Button type="primary" onClick={applyModalSelection}>
                      OK
                    </Button>
                  </div>
                </div>
              </ModalWindow>
            </Portal>
          )}
        </DivE>
      )
    );
  }

  return (
    wrapperForm(
      <Select
        key={key} style={styl} className={clsu.join(" ")} uk-tooltip={tooltip}
        options={items}
        value={selValue}
        // defaultValue={selValue}
        mode={selectType.mode}
        size={selectType.size}
        prefix={prefix}
        suffixIcon={rico}
        onChange={onChange}
        filterOption={filterOption}
        placeholder={placeholder}
        placement={selectType.placement}
        allowClear={selectType.allowClear}
        showSearch={selectType.showSearch}
        autoFocus={selectType.autoFocus}
        variant={selectType.variant}
        virtual={selectType.virtual}
        maxCount={selectType.maxCount}
        maxTagCount={selectType.maxTagCount}
        popupMatchSelectWidth={selectType.popupMatchSelectWidth}
        listHeight={Number(elState.attributes.listHeight) || selectType.listHeight}
        listItemHeight={elState.attributes.listItemHeight || selectType.listItemHeight}
        disabled={elState?.disabled || permissions.disabled === true}
        popupRender={dropdownRender}
        // getPopupContainer={(trigger) => (document.getElementById("virtual-desktop"))}
      />
    )
  );
}

export default RZselect;
/* eslint-enable @typescript-eslint/no-unused-vars */
